import { useTheme, getColors } from "../ThemeContext";   

const MONO = "'Space Mono', monospace";
const SANS = "'Syne', sans-serif";

const C = {
  bg:"#050b14",panel:"#080f1e",card:"#0b1525",border:"#0f2040",border2:"#162a4a",
  cyan:"#00d4ff",teal:"#00ffcc",amber:"#ffb84d",pink:"#ff4da6",purple:"#a855f7",
  blue:"#3b9eff",red:"#ff3d5a",green:"#00ff88",orange:"#ff7c2a",
  text:"#e2eaf8",muted:"#4a6080",muted2:"#2a3f60",
};
function applyTheme(colors) { Object.assign(C, colors); }  
function SpecsPanel({ wellSpecs, modelInfo, predictions }) {
  const specs = wellSpecs || {};
  const info  = modelInfo  || {};

  const sortedPredictions = predictions?.predictions
    ? [...predictions.predictions].sort((a,b)=>{
        const na=(a.well_name||"").toString().split("/").pop();
        const nb=(b.well_name||"").toString().split("/").pop();
        const aFirst=na==="WELL_1"||na.endsWith("_1")?0:1;
        const bFirst=nb==="WELL_1"||nb.endsWith("_1")?0:1;
        if(aFirst!==bFirst) return aFirst-bFirst;
        return na.localeCompare(nb);
      })
    : null;

  return (
    <div style={{ background:C.card, border:`1px solid ${C.border}`, borderRadius:14, padding:"22px 20px", animation:"fade-up 0.5s ease 0.2s both" }}>
      <div style={{ fontFamily:SANS, fontWeight:700, fontSize:14, marginBottom:20 }}>Well Specs & Model Config</div>
      {sortedPredictions && sortedPredictions.length > 0 ? (
        sortedPredictions.map((item,idx)=>(
          <div key={idx} style={{ marginBottom:24, padding:"16px 18px", background:C.panel, border:`1px solid ${C.border2}`, borderRadius:12 }}>
            <div style={{ fontFamily:SANS, fontWeight:700, fontSize:13, color:C.cyan, marginBottom:12 }}>{item.well_name}</div>
            <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:"12px 20px", fontSize:11 }}>
              {Object.entries(specs).filter(([k])=>k!=="note").map(([k,v])=>(
                <div key={k} style={{ display:"flex", justifyContent:"space-between" }}>
                  <span style={{ color:C.muted }}>{k.replace(/_/g," ")}</span>
                  <span style={{ color:C.cyan, fontFamily:MONO }}>{v}</span>
                </div>
              ))}
              <div style={{ gridColumn:"1 / -1", height:1, background:C.border, margin:"8px 0" }}/>
              {[
                ["Input Features",  info.input_features?.length??", "],
                ["Target Features", info.target_features?.length??", "],
                ["Hidden Size",     info.hidden_size??", "],
                ["Num Layers",      info.num_layers??", "],
                ["Output Size",     info.output_size??", "],
                ["Window Size",     info.window_size??", "],
              ].map(([label,val])=>(
                <div key={label} style={{ display:"flex", justifyContent:"space-between" }}>
                  <span style={{ color:C.muted }}>{label}</span>
                  <span style={{ color:C.cyan, fontFamily:MONO }}>{val}</span>
                </div>
              ))}
            </div>
          </div>
        ))
      ) : (
        <div style={{ textAlign:"center", padding:"60px 20px", color:C.muted, fontFamily:MONO, fontSize:12 }}>
          Click <span style={{ color:C.purple }}>RUN PREDICT</span> to see per-well specs
        </div>
      )}
    </div>
  );
}

function PredictionsPanel({ predictions, loading, onRun }) {
  let predList = [];
  if(predictions) {
    if(Array.isArray(predictions))                                   predList=predictions;
    else if(predictions.wells&&Array.isArray(predictions.wells))     predList=predictions.wells;
    else if(predictions.predictions&&Array.isArray(predictions.predictions)) predList=predictions.predictions;
  }
  predList=[...predList].sort((a,b)=>{
    const nameA=(a.well_name||a.well||"").toString().split("/").pop();
    const nameB=(b.well_name||b.well||"").toString().split("/").pop();
    const aIsFirst=nameA.endsWith("_1")||nameA==="WELL_1"?0:1;
    const bIsFirst=nameB.endsWith("_1")||nameB==="WELL_1"?0:1;
    if(aIsFirst!==bIsFirst) return aIsFirst-bIsFirst;
    return nameA.localeCompare(nameB);
  });

  return (
    <div style={{ background:C.card, border:`1px solid ${C.border}`, borderRadius:14, padding:"22px 20px", minWidth:0 }}>
      <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:20, flexWrap:"wrap", gap:10 }}>
        <div>
          <div style={{ fontFamily:SANS, fontWeight:700, fontSize:14 }}>AI Model Predictions</div>
          <div style={{ fontFamily:MONO, fontSize:10, color:C.muted, marginTop:3 }}>AI Model, Next cycle forecast per well</div>
        </div>
        <button onClick={onRun} disabled={loading} style={{
          background:loading?C.muted2:`linear-gradient(135deg, ${C.purple}, ${C.pink})`,
          color:"#fff", border:"none", borderRadius:8, padding:"9px 20px",
          fontFamily:MONO, fontSize:11, fontWeight:700, letterSpacing:1,
          cursor:loading?"not-allowed":"pointer",
        }}>
          {loading?"Running Model...":"▶ RUN PREDICTIONS"}
        </button>
      </div>

      {loading && <div style={{ padding:"80px 20px", textAlign:"center", color:C.muted, fontFamily:MONO }}>Running AI Model...</div>}

      {!loading && predList.length===0 && (
        <div style={{ textAlign:"center", padding:"100px 20px", fontFamily:MONO, fontSize:13, color:C.muted }}>
          No predictions available.<br/>Click <span style={{ color:C.purple, fontWeight:700 }}>RUN PREDICTIONS</span> above
        </div>
      )}

      {!loading && predList.length>0 && (
        <div style={{ display:"flex", flexDirection:"column", gap:16 }}>
          {predList.map((item,i)=>{
            const shortName=(item.well_name||item.well||"Unknown Well").toString().split("/").pop();
            const modelPred=item.model_prediction||{};
            const arrivedRaw=modelPred.PlungerArrived||item.PlungerArrived||"No";
            const arrived=String(arrivedRaw).trim().toLowerCase()==="yes"?"Yes":"No";
            return (
              <div key={i} style={{
                background:C.panel,
                border:`1px solid ${arrived==="Yes"?C.green:C.red}44`,
                borderRadius:12, padding:18,
              }}>
                <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:14 }}>
                  <div style={{ fontFamily:SANS, fontWeight:700, fontSize:14, color:C.cyan }}>{shortName}</div>
                  <div style={{
                    padding:"4px 16px", borderRadius:20, fontFamily:MONO, fontSize:12, fontWeight:700,
                    background:arrived==="Yes"?`${C.green}20`:`${C.red}20`,
                    color:arrived==="Yes"?C.green:C.red,
                  }}>Plunger Arrived: {arrived}</div>
                </div>
                {item.model_prediction && (
                  <div style={{ marginTop:10, paddingTop:10, borderTop:`1px solid ${C.border}` }}>
                    <div style={{ fontFamily:MONO, fontSize:10, color:C.muted, marginBottom:6 }}>AI MODEL PREDICTION (Next Cycle)</div>
                    {Object.entries(item.model_prediction).map(([key,val])=>{
                      if(key==="PlungerArrived") return null;
                      return (
                        <div key={key} style={{ display:"flex", justifyContent:"space-between", fontSize:11, padding:"2px 0" }}>
                          <span style={{ color:C.muted }}>{key.replace(/_/g," ")}</span>
                          <span style={{ color:C.text }}>{typeof val==="number"?val.toFixed(3):val}</span>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

export default function ModelPage({ wellSpecs, modelInfo, predictions, predLoading, onRunPredict }) {
  const { isDark } = useTheme();          
  applyTheme(getColors(isDark));         
  return (
    <div style={{ display:"grid", gridTemplateColumns:"auto 1fr", gap:20 }}>
      <SpecsPanel wellSpecs={wellSpecs} modelInfo={modelInfo} predictions={predictions}/>
      <PredictionsPanel predictions={predictions} loading={predLoading} onRun={onRunPredict}/>
    </div>
  );
}
